import React, { useState, useEffect } from "react";
import {
	Users,
	Copy,
	Share2,
	UserMinus,
	LogOut,
	Plus,
	Crown,
	User as UserIcon,
	Trash2,
} from "lucide-react";
import { useApp } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import { useGetCurrentGroup } from "../api/groups/useGetCurrentGroup";
import { useLeaveGroup } from "../api/groups/useLeaveGroup";
import { useDeleteGroup } from "../api/groups/useDeleteGroup";
import { useRemoveMember } from "../api/groups/useRemoveMember";
import { useAppRouter } from "../hooks/useAppRouter";
import { useTranslation } from "../i18n/TranslationContext";
import { showSuccess, showError } from "../services/notification.service";

interface GroupMember {
	id: string;
	name: string;
	email: string;
	age: number;
	nationality: string;
	initials: string;
	joinedAt: string;
	isOwner: boolean;
	isSelf: boolean;
}

interface Group {
	id: string;
	name: string;
	description: string;
	groupNumber: string;
	privacy: "public" | "private";
	ownerId: string;
	members: GroupMember[];
	maxMembers: number;
}

export function GroupManagement() {
	const { navigateDirectly, updateUserCompletionState } = useApp();
	const router = useAppRouter();
	const { t } = useTranslation();
	const {
		group: apiGroup,
		loading: groupLoading,
		refetch: refetchGroup,
	} = useGetCurrentGroup();
	const { leaveGroup: apiLeaveGroup, loading: leaveLoading } = useLeaveGroup();
	const { deleteGroup: apiDeleteGroup, loading: deleteLoading } =
		useDeleteGroup();
	const { removeMember: apiRemoveMember, loading: removeMemberLoading } =
		useRemoveMember();
	const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
	const [isOwner, setIsOwner] = useState(false);
	const [error, setError] = useState<string>("");
	const [copySuccess, setCopySuccess] = useState(false);
	const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [showRemoveMemberConfirmation, setShowRemoveMemberConfirmation] =
		useState(false);
	const [memberToRemove, setMemberToRemove] = useState<GroupMember | null>(
		null
	);
	const [showInviteModal, setShowInviteModal] = useState(false);
	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteLoading, setInviteLoading] = useState(false);
	const [inviteError, setInviteError] = useState("");
	const [checkingEmail, setCheckingEmail] = useState(false);

	useEffect(() => {
		// GroupRouter now handles the navigation logic
		// This component only renders when user has a group
		if (apiGroup && apiGroup.group_id) {
			// Convert API group to display format
			const enrichedGroup: Group = {
				id: apiGroup.group_id.toString(),
				name: apiGroup.group_name || t("groupManagement.unnamedGroup"),
				description: apiGroup.description || "",
				groupNumber: apiGroup.group_code?.toString() || "",
				privacy: "public",
				ownerId: apiGroup.extra_id?.toString() || "",
				maxMembers: 4,
				members: apiGroup.members
					? apiGroup.members.map((member) => {
							// Calculate age from date_of_birth if available
							const age = member.date_of_birth
								? new Date().getFullYear() -
									new Date(member.date_of_birth).getFullYear()
								: 25 + Math.floor(Math.random() * 15);

							// Generate initials from name
							const initials =
								`${member.first_name?.charAt(0) || ""}${member.last_name?.charAt(0) || ""}`.toUpperCase();

							return {
								id: member.extra_id.toString(),
								name:
									`${member.first_name || ""} ${member.last_name || ""}`.trim() ||
									t("groupManagement.groupCreator"),
								email: member.email || "",
								age: age,
								nationality:
									member.nationality || t("groupManagement.defaultNationality"),
								initials: initials || "GM",
								joinedAt: new Date().toISOString(), // We don't have join timestamp yet
								isOwner: member.is_owner,
								isSelf:
									member.extra_id.toString() === apiGroup.extra_id?.toString(),
							};
						})
					: [],
			};

			setCurrentGroup(enrichedGroup);
			// Check if current user is the owner using role
			setIsOwner(apiGroup.role === "creator");
		}
	}, [apiGroup, groupLoading]);

	const handleKickMember = (memberId: string) => {
		const member = currentGroup?.members.find((m) => m.id === memberId);
		if (!member) return;
		setMemberToRemove(member);
		setShowRemoveMemberConfirmation(true);
	};

	const handleConfirmRemoveMember = async () => {
		if (!memberToRemove || !currentGroup) return;

		setShowRemoveMemberConfirmation(false);

		const success = await apiRemoveMember(
			parseInt(currentGroup.id),
			parseInt(memberToRemove.id)
		);

		if (success) {
			// Refresh group data to update member list
			await refetchGroup();
			setMemberToRemove(null);
		} else {
			// Error is handled by the hook, but we can clear the member selection
			setMemberToRemove(null);
		}
	};

	const handleLeaveGroup = () => {
		setShowLeaveConfirmation(true);
	};

	const handleDeleteGroup = () => {
		setShowDeleteConfirmation(true);
	};

	const handleConfirmLeave = async () => {
		setShowLeaveConfirmation(false);
		const success = await apiLeaveGroup();
		if (success) {
			// Update completion state
			updateUserCompletionState({ group_member: false });

			// Navigate to group selection
			router.navigate("app-group-select");
		}
	};

	const handleConfirmDelete = async () => {
		if (!currentGroup) return;

		setShowDeleteConfirmation(false);
		const success = await apiDeleteGroup(parseInt(currentGroup.id));
		if (success) {
			// Update completion state
			updateUserCompletionState({ group_member: false });

			// Navigate to group selection
			router.navigate("app-group-select");
		}
	};

	const handleCopyGroupNumber = () => {
		if (!currentGroup) return;

		navigator.clipboard
			.writeText(currentGroup.groupNumber)
			.then(() => {
				setCopySuccess(true);
				setTimeout(() => setCopySuccess(false), 2000);
			})
			.catch(() => {
				setError(t("groupManagement.failedToCopy"));
			});
	};

	const handleShareGroup = () => {
		if (!currentGroup) return;

		const shareData = {
			title: `Join my group: ${currentGroup.name}`,
			text: `Join my group "${currentGroup.name}" for the UCM Airport events! Group number: ${currentGroup.groupNumber}`,
			url: window.location.href,
		};

		if (navigator.share && navigator.canShare?.(shareData)) {
			navigator.share(shareData);
		} else {
			// Fallback: copy to clipboard
			const shareText = `${shareData.text}`;
			navigator.clipboard.writeText(shareText).then(() => {
				setCopySuccess(true);
				setTimeout(() => setCopySuccess(false), 2000);
			});
		}
	};

	// Email validation function
	const validateEmail = (email: string): string | null => {
		// Check if email is empty
		if (!email) {
			return t("groupManagement.emailRequired");
		}

		// More comprehensive email regex pattern
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		if (!emailRegex.test(email)) {
			return t("groupManagement.invalidEmailFormat");
		}

		// Check for common invalid patterns
		if (email.includes("..") || email.startsWith(".") || email.endsWith(".")) {
			return t("groupManagement.invalidEmailFormat");
		}

		// Check minimum length
		if (email.length < 5) {
			return t("groupManagement.emailTooShort");
		}

		// Check maximum length (RFC 5321)
		if (email.length > 254) {
			return t("groupManagement.emailTooLong");
		}

		return null; // Email is valid
	};

	const checkEmailExists = async (email: string): Promise<boolean> => {
		try {
			setCheckingEmail(true);
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/auth/check-email`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ email }),
				}
			);

			if (!response.ok) {
				return false;
			}

			const data = await response.json();
			const status = data?.data?.status || data?.status;

			switch (status) {
				case "verified":
					// User exists and is verified - can be invited
					return true;

				case "unverified":
					// User exists but needs verification
					setInviteError(t("groupManagement.userNotVerified"));
					return false;

				case "external_user":
				case "not_found":
					// External user or user doesn't exist - show same message
					setInviteError(t("groupManagement.userNotFound"));
					return false;

				default:
					// Unexpected status
					setInviteError(t("groupManagement.invitationFailed"));
					return false;
			}
		} catch (error) {
			console.error("Failed to check email:", error);
			setInviteError(t("groupManagement.invitationFailed"));
			return false;
		} finally {
			setCheckingEmail(false);
		}
	};

	const handleInviteMember = async () => {
		if (!currentGroup) return;

		// Validate email
		const validationError = validateEmail(inviteEmail);
		if (validationError) {
			setInviteError(validationError);
			return;
		}

		// Check if email exists before sending invitation
		const emailExists = await checkEmailExists(inviteEmail);

		if (!emailExists) {
			// Error is already set by checkEmailExists
			return;
		}

		setInviteLoading(true);
		setInviteError("");

		try {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/groups/${currentGroup.groupNumber}/invite`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
					},
					body: JSON.stringify({ email: inviteEmail }),
				}
			);

			const data = await response.json();

			if (response.ok && data.success) {
				// Success - close modal and reset
				setShowInviteModal(false);
				setInviteEmail("");

				// Show success message and refresh group data
				if (data.data?.userAdded && data.data?.emailSent) {
					showSuccess(t("groupManagement.userAddedAndInvited"));
				} else if (data.data?.userAdded) {
					showSuccess(t("groupManagement.userAddedToGroup"));
				} else {
					showSuccess(t("groupManagement.invitationSentSuccess"));
				}

				// Refresh group data to show the new member
				refetchGroup();
			} else {
				// Check for specific error messages
				if (
					data.error &&
					data.error.toLowerCase().includes("already a member")
				) {
					setInviteError(t("groupManagement.userAlreadyInGroup"));
				} else {
					setInviteError(data.error || t("groupManagement.invitationFailed"));
				}
			}
		} catch (error) {
			setInviteError(t("groupManagement.invitationFailed"));
		} finally {
			setInviteLoading(false);
		}
	};

	if (!currentGroup) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="text-center py-8">
					<p className="text-[var(--ucm-text-medium)]">
						{t("groupManagement.loading")}
					</p>
				</div>
			</MainLayout>
		);
	}

	// Sort members: owner first, then by join time
	const sortedMembers = currentGroup.members.sort((a, b) => {
		if (a.isOwner && !b.isOwner) return -1;
		if (!a.isOwner && b.isOwner) return 1;
		return new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime();
	});

	const emptySlots = Math.max(
		0,
		currentGroup.maxMembers - currentGroup.members.length
	);
	const isGroupFull = currentGroup.members.length >= currentGroup.maxMembers;

	return (
		<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
			<div className="space-y-6">
				{/* Group Header */}
				<div className="text-center">
					<div className="w-16 h-16 bg-[var(--ucm-success-green)] rounded-full flex items-center justify-center mx-auto mb-4">
						<Users size={32} className="text-white" />
					</div>
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
						{currentGroup.name}
					</h1>
					<div className="flex items-center justify-center space-x-4 text-sm text-[var(--ucm-text-medium)] mb-3">
						<span>Gruppe #{currentGroup.groupNumber}</span>
						<span>•</span>
						<span>
							{currentGroup.members.length}/{currentGroup.maxMembers}{" "}
							{t("groupManagement.maxMembers")}
						</span>
					</div>
					{currentGroup.description && (
						<p className="text-[var(--ucm-text-medium)] px-4">
							{currentGroup.description}
						</p>
					)}
				</div>

				{/* Members Section */}
				<div className="space-y-4">
					<h3 className="font-semibold text-[var(--ucm-text-dark)]">
						{t("groupManagement.groupMembers")}
					</h3>

					{/* Member Cards */}
					{sortedMembers.map((member) => (
						<div
							key={member.id}
							className={`bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(0,0,0,0.08)] border-l-4 ${
								member.isOwner
									? "border-[var(--ucm-primary-teal)] bg-gradient-to-r from-[rgba(15,32,39,0.02)] to-white"
									: member.isSelf
										? "border-[var(--ucm-info-blue)] bg-gradient-to-r from-[rgba(59,130,246,0.02)] to-white"
										: "border-transparent"
							}`}
						>
							<div className="flex items-center space-x-4">
								{/* Member Avatar */}
								<div
									className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 ${
										member.isOwner
											? "bg-[var(--ucm-primary-teal)]"
											: member.isSelf
												? "bg-[var(--ucm-info-blue)]"
												: "bg-[var(--ucm-success-green)]"
									}`}
								>
									{member.initials}
								</div>

								{/* Member Info */}
								<div className="flex-1">
									<div className="flex items-center space-x-2 mb-1">
										<h4 className="font-semibold text-[var(--ucm-text-dark)]">
											{member.name}
										</h4>
										{member.isOwner && (
											<Crown
												size={16}
												className="text-[var(--ucm-primary-teal)]"
											/>
										)}
										{member.isSelf && !member.isOwner && (
											<UserIcon
												size={16}
												className="text-[var(--ucm-info-blue)]"
											/>
										)}
									</div>
									<p className="text-xs font-medium text-[var(--ucm-text-medium)] uppercase mb-1">
										{member.isOwner
											? t("groupManagement.groupLeader")
											: member.isSelf
												? t("groupManagement.you")
												: t("groupManagement.member")}
									</p>
									<p className="text-sm text-[var(--ucm-text-medium)]"></p>
								</div>

								{/* Member Actions */}
								{isOwner && !member.isSelf && (
									<button
										onClick={() => handleKickMember(member.id)}
										disabled={removeMemberLoading}
										className="p-2 text-[var(--ucm-error-red)] hover:bg-[var(--ucm-light-red)] hover:text-[var(--ucm-error-red)] rounded-lg transition-colors duration-200 disabled:opacity-50"
										title={t("groupManagement.removeMember")}
									>
										<UserMinus size={16} />
									</button>
								)}
							</div>
						</div>
					))}

					{/* Empty Slots (Owner only, if group not full) */}
					{isOwner &&
						!isGroupFull &&
						Array.from({ length: emptySlots }).map((_, index) => (
							<div
								key={index}
								onClick={() => setShowInviteModal(true)}
								className="bg-[var(--ucm-disabled-gray)] border-2 border-dashed border-[var(--ucm-border-medium)] rounded-2xl p-4 cursor-pointer hover:bg-[var(--ucm-light-gray)] transition-colors"
							>
								<div className="flex items-center space-x-4">
									<div className="w-12 h-12 rounded-full bg-[var(--ucm-border-medium)] flex items-center justify-center flex-shrink-0">
										<Plus size={24} className="text-[var(--ucm-text-light)]" />
									</div>
									<div className="flex-1">
										<p className="text-sm text-[var(--ucm-text-medium)] mb-2">
											{t("groupManagement.inviteMoreMembers")}
										</p>
										<button
											onClick={handleCopyGroupNumber}
											className="text-xs bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] px-3 py-1 rounded-full hover:bg-gray-50 transition-colors duration-200"
										>
											{copySuccess
												? t("groupManagement.copied")
												: t("groupManagement.shareGroupNumber")}
										</button>
									</div>
								</div>
							</div>
						))}
				</div>

				{/* Invite Section (Owner only, if group not full) */}
				{isOwner && !isGroupFull && (
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						<h3 className="font-semibold text-[var(--ucm-text-dark)] mb-4">
							{t("groupManagement.inviteMembers")}
						</h3>
						<p className="text-sm text-[var(--ucm-text-medium)] mb-4">
							{t("groupManagement.inviteDescription")}
						</p>

						<div className="flex items-center space-x-3 mb-4">
							<div className="flex-1 p-3 bg-[var(--ucm-light-blue)] rounded-xl">
								<p className="text-center font-mono text-lg font-bold text-[var(--ucm-dark-blue)]">
									{currentGroup.groupNumber}
								</p>
							</div>
						</div>

						<div className="flex space-x-3">
							<button
								onClick={handleCopyGroupNumber}
								className="flex-1 h-10 bg-[var(--ucm-primary-teal)] text-white font-medium rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 flex items-center justify-center space-x-2"
							>
								<Copy size={16} />
								<span>
									{copySuccess
										? t("groupManagement.copied")
										: t("groupManagement.copyNumber")}
								</span>
							</button>
							<button
								onClick={handleShareGroup}
								className="h-10 px-4 bg-[var(--ucm-success-green)] text-white font-medium rounded-full hover:bg-[var(--ucm-dark-green)] transition-colors duration-200 flex items-center justify-center"
							>
								<Share2 size={16} />
							</button>
						</div>
					</div>
				)}

				{/* Group Full Message */}
				{isGroupFull && (
					<div className="bg-[var(--ucm-light-yellow)] border border-[var(--ucm-warning-yellow)] rounded-2xl p-4">
						<p className="text-center text-[var(--ucm-dark-yellow)] font-medium">
							{t("groupManagement.groupFull")} (
							{t("groupManagement.membersCount", {
								count: currentGroup.members.length,
								max: currentGroup.maxMembers,
							})}
							)
						</p>
					</div>
				)}

				{/* Error Display */}
				{error && (
					<div className="p-3 bg-[var(--ucm-error-red)] bg-opacity-10 border border-[var(--ucm-error-red)] rounded-xl">
						<p className="text-sm text-[var(--ucm-error-red)]">{error}</p>
					</div>
				)}

				{/* Group Actions */}
				<div className="space-y-3">
					{!isOwner && (
						<button
							onClick={handleLeaveGroup}
							disabled={leaveLoading}
							className="w-full h-12 bg-[var(--ucm-error-red)] text-white font-semibold rounded-full hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
						>
							<LogOut size={16} />
							<span>
								{leaveLoading
									? t("groupManagement.leaving")
									: t("groupManagement.leaveGroup")}
							</span>
						</button>
					)}
					{isOwner && (
						<button
							onClick={handleDeleteGroup}
							disabled={deleteLoading}
							className="w-full h-12 bg-[var(--ucm-error-red)] text-white font-semibold rounded-full hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
						>
							<Trash2 size={16} />
							<span>
								{deleteLoading
									? t("groupManagement.deleting")
									: t("groupManagement.deleteGroup")}
							</span>
						</button>
					)}
				</div>
			</div>

			{/* Leave Group Confirmation Modal */}
			{showLeaveConfirmation && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full m-auto">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{t("groupManagement.confirmLeaveTitle")}
						</h2>

						<div className="mb-6">
							<p className="text-[var(--ucm-text-medium)] mb-4">
								{t("groupManagement.confirmLeaveMessage")}{" "}
								<strong>{currentGroup?.name}</strong>?
							</p>

							<div className="bg-[var(--ucm-light-yellow)] border border-[var(--ucm-warning-yellow)] rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<span className="text-xl flex-shrink-0">⚠️</span>
									<div>
										<h4 className="font-semibold text-[var(--ucm-dark-yellow)] mb-1">
											{t("groupManagement.importantNotice")}
										</h4>
										<ul className="text-sm text-[var(--ucm-dark-yellow)] space-y-1">
											<li>• {t("groupManagement.leaveWarnings.loseEventApplications")}</li>
											<li>• {t("groupManagement.leaveWarnings.loseAccess")}</li>
											{isOwner && (
												<li>
													• {t("groupManagement.leaveWarnings.ownerWarning")}
												</li>
											)}
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => setShowLeaveConfirmation(false)}
								disabled={leaveLoading}
								className="flex-1 bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] rounded-xl p-3 font-medium transition-colors hover:bg-[var(--ucm-light-gray)] disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t("groupManagement.cancel")}
							</button>
							<button
								onClick={handleConfirmLeave}
								disabled={leaveLoading}
								className="flex-1 bg-[var(--ucm-error-red)] text-white rounded-xl p-3 font-medium transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
							>
								{leaveLoading ? (
									<span>{t("groupManagement.leaving")}</span>
								) : (
									<>
										<LogOut size={16} />
										<span>{t("groupManagement.leaveGroup")}</span>
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Group Confirmation Modal */}
			{showDeleteConfirmation && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full m-auto">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{t("groupManagement.confirmDeleteTitle")}
						</h2>

						<div className="mb-6">
							<p className="text-[var(--ucm-text-medium)] mb-4">
								{t("groupManagement.confirmDeleteMessage", {
									group_name: currentGroup?.name,
								})}
							</p>

							<div className="bg-[var(--ucm-light-red)] border border-[var(--ucm-error-red)] rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<span className="text-xl flex-shrink-0">🚨</span>
									<div>
										<h4 className="font-semibold text-[var(--ucm-error-red)] mb-1">
											{t("groupManagement.warningTitle")}
										</h4>
										<ul className="text-sm text-[var(--ucm-error-red)] space-y-1">
											<li>• {t("groupManagement.deleteWarnings.permanent")}</li>
											<li>
												• {t("groupManagement.deleteWarnings.membersRemoved")}
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => setShowDeleteConfirmation(false)}
								disabled={deleteLoading}
								className="flex-1 bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] rounded-xl p-3 font-medium transition-colors hover:bg-[var(--ucm-light-gray)] disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t("groupManagement.cancel")}
							</button>
							<button
								onClick={handleConfirmDelete}
								disabled={deleteLoading}
								className="flex-1 bg-[var(--ucm-error-red)] text-white rounded-xl p-3 font-medium transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
							>
								{deleteLoading ? (
									<span>{t("groupManagement.deleting")}</span>
								) : (
									<>
										<Trash2 size={16} />
										<span>{t("groupManagement.deleteGroup")}</span>
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Remove Member Confirmation Modal */}
			{showRemoveMemberConfirmation && memberToRemove && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full m-auto">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{t("groupManagement.confirmRemoveMemberTitle")}
						</h2>

						<div className="mb-6">
							<p className="text-[var(--ucm-text-medium)] mb-4">
								{t("groupManagement.confirmRemoveMemberMessage")}{" "}
								<strong>{memberToRemove.name}</strong>?
							</p>

							<div className="bg-[var(--ucm-light-red)] border border-[var(--ucm-error-red)] rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<span className="text-xl flex-shrink-0">⚠️</span>
									<div>
										<h4 className="font-semibold text-[var(--ucm-error-red)] mb-1">
											{t("groupManagement.warningTitle")}
										</h4>
										<ul className="text-sm text-[var(--ucm-error-red)] space-y-1">
											<li>
												•{" "}
												{t(
													"groupManagement.removeWarnings.memberWillBeRemoved"
												)}
											</li>
											<li>
												•{" "}
												{t("groupManagement.removeWarnings.memberNeedsNewCode")}
											</li>
											<li>
												• {t("groupManagement.removeWarnings.cannotUndo")}
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => {
									setShowRemoveMemberConfirmation(false);
									setMemberToRemove(null);
								}}
								disabled={removeMemberLoading}
								className="flex-1 bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] rounded-xl p-3 font-medium transition-colors hover:bg-[var(--ucm-light-gray)] disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t("groupManagement.cancel")}
							</button>
							<button
								onClick={handleConfirmRemoveMember}
								disabled={removeMemberLoading}
								className="flex-1 bg-[var(--ucm-error-red)] text-white rounded-xl p-3 font-medium transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
							>
								{removeMemberLoading ? (
									<span>{t("groupManagement.removingMember")}</span>
								) : (
									<>
										<UserMinus size={16} />
										<span>{t("groupManagement.removeMember")}</span>
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Invite Member Modal */}
			{showInviteModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full m-auto">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{t("groupManagement.inviteMember")}
						</h2>

						<div className="mb-6">
							<p className="text-[var(--ucm-text-medium)] mb-4">
								{t("groupManagement.inviteModalDescription")}
							</p>

							<div className="space-y-4">
								<div>
									<label
										htmlFor="inviteEmail"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("groupManagement.emailAddress")}
									</label>
									<div className="relative">
										<input
											type="email"
											id="inviteEmail"
											value={inviteEmail}
											onChange={(e) => {
												const newEmail = e.target.value;
												setInviteEmail(newEmail);
												// Clear error when user starts typing
												if (inviteError) {
													setInviteError("");
												}
											}}
											placeholder={t("groupManagement.emailPlaceholder")}
											className={`w-full px-4 py-3 border ${
												inviteError
													? "border-[var(--ucm-error-red)] focus:ring-[var(--ucm-error-red)]"
													: "border-[var(--ucm-border-light)] focus:ring-[var(--ucm-primary-teal)]"
											} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-colors`}
											disabled={inviteLoading || checkingEmail}
										/>
										{checkingEmail && (
											<div className="absolute right-3 top-1/2 -translate-y-1/2">
												<div className="w-5 h-5 border-2 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin"></div>
											</div>
										)}
									</div>
								</div>

								{inviteError && (
									<div className="text-sm text-[var(--ucm-error-red)]">
										{inviteError}
									</div>
								)}
							</div>
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => {
									setShowInviteModal(false);
									setInviteEmail("");
									setInviteError("");
								}}
								disabled={inviteLoading}
								className="flex-1 bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] rounded-xl p-3 font-medium transition-colors hover:bg-[var(--ucm-light-gray)] disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t("groupManagement.cancel")}
							</button>
							<button
								onClick={handleInviteMember}
								disabled={
									inviteLoading ||
									!inviteEmail ||
									checkingEmail ||
									!!inviteError
								}
								className="flex-1 bg-[var(--ucm-primary-teal)] text-white rounded-xl p-3 font-medium transition-colors hover:bg-[var(--ucm-primary-teal-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{inviteLoading
									? t("groupManagement.sending")
									: t("groupManagement.sendInvitation")}
							</button>
						</div>
					</div>
				</div>
			)}
		</MainLayout>
	);
}
